const purchasesApi = require('../api/purchases');

const { addCards, shredCardSuccessful } = require('../slices/cards/actions');
const {
  updateLootcoinBalance,
  updateLootgemBalance,
  updateTicketBalance,
  updateBalance,
} = require('./helpers');

const MIN_PURCHASES_UPDATE = 1000 * 60 * 5; // 5 minutes
const FETCH_PURCHASES_TIMEOUT = 1000 * 30; // 30 seconds

function fetchPurchasesInProgress() {
  return {
    type: 'FETCH_PURCHASES_IN_PROGRESS',
    timestamp: Date.now(),
  };
}

function fetchPurchasesSuccessful(purchases) {
  return {
    type: 'FETCH_PURCHASES_SUCCESSFUL',
    purchases,
    timestamp: Date.now(),
  };
}

function fetchPurchasesFailed(reason) {
  return {
    type: 'FETCH_PURCHASES_FAILED',
    reason,
    timestamp: Date.now(),
  };
}

function fetchPurchase(purchaseId) {
  return (dispatch, getState) => {
    const state = getState();

    return purchasesApi
      .fetchPurchase(state.auth.apiUrl, state.auth.token, purchaseId)
      .then((purchase) => {
        dispatch({
          type: 'UPDATE_PURCHASE',
          purchase,
          timestamp: Date.now(),
        });
        return purchase;
      });
  };
}

function fetchPurchases() {
  return (dispatch, getState) => {
    dispatch(fetchPurchasesInProgress());

    const state = getState();

    if (!state.auth.token || !state.auth.apiUrl) {
      return Promise.resolve();
    }

    return purchasesApi
      .fetchPurchases(state.auth.apiUrl, state.auth.token)
      .then(
        (purchases) => {
          dispatch(fetchPurchasesSuccessful(purchases));
          return purchases;
        },
        (error) => {
          dispatch(fetchPurchasesFailed(error));
          throw error;
        },
      );
  };
}

function addPurchase(purchase) {
  return (dispatch) => {
    if (purchase) {
      dispatch({
        type: 'ADD_PURCHASE',
        purchase,
        timestamp: Date.now(),
      });
    }
  };
}

function addPurchases(purchases) {
  return (dispatch) => {
    if (Array.isArray(purchases) && purchases.length) {
      dispatch({
        type: 'ADD_PURCHASES',
        purchases,
        timestamp: Date.now(),
      });
    }
  };
}

function shouldFetchPurchases(state, skipOutdatedCheck) {
  const { apiUrl, token } = state.auth;

  // Only fetch purchases if logged in
  if (!apiUrl || !token || !state.purchases) {
    return false;
  }

  const { fetchingInProgressSince, didInvalidate, lastSuccessfulFetch } =
    state.purchases;

  if (!fetchingInProgressSince) {
    // Should update if it was explicetely invalidated
    if (didInvalidate) {
      return true;
    }

    // Should update if data is too old
    if (
      !skipOutdatedCheck &&
      (!lastSuccessfulFetch ||
        Date.now() - lastSuccessfulFetch > MIN_PURCHASES_UPDATE)
    ) {
      return true;
    }
  } else if (
    fetchingInProgressSince &&
    Date.now() - fetchingInProgressSince > FETCH_PURCHASES_TIMEOUT
  ) {
    // Ensure we cannot get stuck in fetchingInProgressSince=true state.
    return true;
  }

  return false;
}

function fetchPurchasesIfNeeded() {
  return (dispatch, getState) => {
    if (shouldFetchPurchases(getState())) {
      return dispatch(fetchPurchases());
    }
    return Promise.resolve();
  };
}

function invalidatePurchases() {
  return {
    type: 'INVALIDATE_PURCHASES',
    timestamp: Date.now(),
  };
}

function updateIap(purchaseId, updateDetails) {
  return (dispatch, getState) => {
    const state = getState();
    const { token } = state.auth;
    const { apiUrl } = state.auth;

    return purchasesApi
      .updateIap(apiUrl, token, purchaseId, updateDetails)
      .then((bundlePurchase) => {
        const {
          purchases,
          newLootcoinBalance,
          newLootgemBalance,
          newTicketBalance,
        } = bundlePurchase || {};

        const [purchase] = purchases || [];
        if (purchase && purchase.status === 'PAID') {
          dispatch(updateLootcoinBalance(newLootcoinBalance));
          if (typeof newLootgemBalance === 'number') {
            dispatch(updateLootgemBalance({ newLootgemBalance }));
          }
          if (typeof newTicketBalance === 'number') {
            dispatch(updateTicketBalance({ newTicketBalance }));
          }
          dispatch(
            addPurchases((purchases || []).filter((p) => p.status === 'PAID')),
          );
        }
        return purchases;
      });
  };
}

function openPurchase(purchaseId) {
  return (dispatch, getState) => {
    const state = getState();

    return purchasesApi
      .openPurchase(state.auth.apiUrl, state.auth.token, purchaseId)
      .then((result) => {
        if (!result.alreadyOpened) {
          dispatch(addCards(result.cards));
        }
        dispatch(updateLootcoinBalance(result.newLootcoinBalance));
        if (typeof result.newLootgemBalance === 'number') {
          dispatch(updateLootgemBalance(result));
        }
        if (typeof result.newTicketBalance === 'number') {
          dispatch(updateTicketBalance(result));
        }
        dispatch({
          type: 'OPEN_PURCHASE_SUCCESSFUL',
          purchaseId,
          timestamp: Date.now(),
        });
        return result;
      });
  };
}

function setCardsSeen(purchaseId) {
  return (dispatch, getState) => {
    const state = getState();

    dispatch({
      type: 'SET_CARDS_SEEN',
      purchaseId,
      timestamp: Date.now(),
    });

    return purchasesApi.setCardsSeen(
      state.auth.apiUrl,
      state.auth.token,
      purchaseId,
    );
  };
}

function createPurchase(data) {
  return (dispatch, getState) => {
    const state = getState();

    const { apiUrl, token } = state.auth;

    if (!apiUrl || !token) {
      return Promise.resolve();
    }

    return purchasesApi.createPurchase(apiUrl, token, data).then((response) => {
      if (response && response.purchase) {
        dispatch(addPurchase(response.purchase));
      }
      if (response && response.purchases) {
        dispatch(addPurchases(response.purchases));
      }
      dispatch(updateTicketBalance(response));
      return response;
    });
  };
}

function setLastPaymentMethod(paymentMethod) {
  return {
    type: 'SET_PAYMENT_METHOD',
    paymentMethod,
  };
}

function setLastBundleAmount(bundleAmount) {
  return {
    type: 'SET_LAST_BUNDLE_AMOUNT',
    bundleAmount,
  };
}

function exchangePremiumCard(purchaseId) {
  return (dispatch, getState) => {
    const state = getState();
    const { apiUrl, token } = state.auth;

    return purchasesApi
      .tradePremiumCard(apiUrl, token, purchaseId)
      .then((response) => {
        if (response.card) {
          dispatch(shredCardSuccessful(response.card));
        }
        updateBalance(dispatch, response);
        return response;
      });
  };
}

module.exports = {
  fetchPurchase,
  fetchPurchases,
  fetchPurchasesIfNeeded,
  createPurchase,
  addPurchase,
  addPurchases,
  openPurchase,
  setCardsSeen,
  setLastPaymentMethod,
  setLastBundleAmount,
  invalidatePurchases,
  updateIap,
  exchangePremiumCard,
};
